import { KalturaPpv, KalturaPrice } from '@kaltura-ott/ott-client-types';
import {
  KalturaAsset,
  KalturaLiveAsset,
  KalturaMediaAsset,
  KalturaProgramAsset,
  KalturaRecording,
  KalturaRecordingAsset,
  KalturaRecordingType,
  KalturaSubscription,
} from '@kaltura-ott/ott-client-types/api/types';

import type {
  KuxEpisodeType,
  KuxLinearType,
  KuxLinkAsAssetType,
  KuxMediaAssetType,
  KuxMovieType,
  KuxSubscription,
  KuxPpv,
  KuxProgramType,
  KuxRecord,
  KuxSeriesType,
  KuxPackage,
  KuxPerson,
  MosaicInfoTag,
  KuxLogo,
  LinearChannelType,
  UIDAPresentationTypeEnum,
} from '../interfaces';

import { FilesFormat, KuXDms, MediaType } from './dms';
import { parseMenuItem } from './menu.helper';
import { getDaysBetweenTwoDate } from './records/records.helper';
import { toBoolean } from './utils/common';
import { toKuxImages } from './utils/image';
import {
  getAdult,
  getParentalRating,
  normalizeCategoryMetas,
  normalizeCategoryTags,
  parseTagKeyValue,
  KuxCategoryMetas,
  KalturaCategoryMetas,
  KuxMultiTags,
} from './utils/meta';
import { KalturaCategoryTags } from './utils/tags';

export const getEpisodeMetas = (
  metas: KuxCategoryMetas,
): { seriesId?: string; episodeNumber?: number; seasonNumber?: number; episodeName?: string } => {
  const seriesId = metas['SeriesID'] as string;
  let episodeNumber = metas['EpisodeNumber'] !== undefined ? Number(metas['EpisodeNumber']) : undefined;
  let seasonNumber = metas['SeasonNumber'] !== undefined ? Number(metas['SeasonNumber']) : undefined;
  const episodeName = metas['EpisodeName'] as string;
  if (seriesId && episodeNumber === 9999 && seasonNumber === 1) {
    episodeNumber = undefined;
    seasonNumber = undefined;
  }
  return {
    seriesId,
    episodeNumber,
    seasonNumber,
    episodeName,
  };
};

const MOSAIC_ASSET_TAGS = ['hidden', 'video', 'mosaic'];

export const getMosaicTags = (tags: KuxMultiTags): MosaicInfoTag => {
  const res = {} as MosaicInfoTag;

  if (tags['MosaicInfo']) {
    tags['MosaicInfo'].forEach((tag) => {
      if (tag.indexOf('=') !== -1) {
        const values = parseTagKeyValue(tag) as Record<string, string>;
        if (values.MosaicProgramExternalId) res.mosaicProgramExternalId = values.MosaicProgramExternalId;
        if (values.SourceProgramExternalId) res.sourceProgramExternalId = values.SourceProgramExternalId;
      } else if (MOSAIC_ASSET_TAGS.includes(tag)) {
        res.type = tag as LinearChannelType;
      }
    });
  }

  if (tags['MosaicChannelsInfo']) {
    res.mosaicChannelsInfo = [];
    tags['MosaicChannelsInfo'].forEach((tag) => {
      if (tag.indexOf('=') !== -1) {
        const parsed = parseTagKeyValue(tag) as Record<string, string>;
        res.mosaicChannelsInfo?.push({
          order: Number(parsed.Order),
          audioLang: parsed.AudioLang,
          channelExternalId: parsed.ChannelExternalId,
          title: parsed.Title,
          programExternalId: parsed.ProgramExternalID,
        });
      }
    });
  }

  return res;
};

export interface AdditionalData {
  newEpisodeBadgeDays?: number;
  recordingExpirationBoundary?: number;
  isGroupedGrid?: boolean;
  presentationType?: UIDAPresentationTypeEnum;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const assetToProgram = (program: KalturaProgramAsset & any, additionalData?: AdditionalData): KuxProgramType => {
  const metas = normalizeCategoryMetas(program.metas as KalturaCategoryMetas);
  const tags = normalizeCategoryTags(program.tags as KalturaCategoryTags);
  const adult = toBoolean(metas['Adult']);
  const recordable = !adult && program.enableCdvr;
  const isRecordProgram = program instanceof KalturaRecordingAsset;
  const startDate = program.startDate * 1000;
  const endDate = program.endDate * 1000;
  const { seriesId, episodeNumber, seasonNumber, episodeName } = getEpisodeMetas(metas);
  return {
    ...(additionalData?.recordingExpirationBoundary
      ? { recordingExpirationBoundary: additionalData?.recordingExpirationBoundary }
      : {}),
    isRecordProgram,
    adult,
    epgId: program.epgId,
    description: program.description,
    duration: endDate - startDate,
    isPopular: toBoolean(metas['Popular']),
    enableCatchUp: program.enableCatchUp,
    enableCdvr: program.enableCdvr,
    enableStartOver: program.enableStartOver,
    enableTrickPlay: program.enableTrickPlay,
    endDate,
    id: program.id,
    images: toKuxImages(program.images),
    linearAssetId: program.linearAssetId,
    metas,
    parentalRating: getParentalRating(tags),
    recordable,
    startDate,
    tags,
    title: program.name,
    type: program.type,
    mediaType: KuXDms.mediaTypesValue[program.type],
    seriesId,
    episodeNumber,
    seasonNumber,
    episodeName,
    newEpisodeBadgeDays: additionalData?.newEpisodeBadgeDays,
    mediaFiles: program?.mediaFiles,
    recordData: {
      recordingId: program?.recordingId && Number(program.recordingId),
      recordingType: program.recordingType as KalturaRecordingType,
      viewableUntilDate: program.viewableUntilDate,
      totalRecordedEpisodes: program?.relatedObjects?.['group_result']?.totalCount,
    },
    externalId: program.externalId,
    mosaicInfo: {
      isMosaicEvent: metas['IsMosaicEvent'] || '0',
      ...getMosaicTags(tags),
    },
    isGroupedSeries: additionalData?.isGroupedGrid || false,
  } as KuxProgramType;
};

export const assetToLinear = (linear: KalturaLiveAsset): KuxLinearType => {
  const metas = normalizeCategoryMetas(linear.metas as KalturaCategoryMetas);
  const tags = normalizeCategoryTags(linear.tags as KalturaCategoryTags);
  const mediaFile = linear.mediaFiles?.find(({ type }) => KuXDms.filesFormat[FilesFormat.live].includes(type));
  return {
    id: linear.id,
    title: linear.name,
    images: toKuxImages(linear.images),
    adult: getAdult(metas),
    catchUpBuffer: linear.catchUpBuffer * 1000 * 60,
    channelNumber: Number(metas['ChannelNumber']),
    description: linear.description,
    mediaFileId: mediaFile?.id,
    parentalRating: getParentalRating(tags),
    representPackage: toBoolean(metas['RepresentPackage']),
    mediaType: KuXDms.mediaTypesValue[linear.type],
    type: linear.type,
    linearChannelType: metas['LinearChannelType'] ? (metas['LinearChannelType'] as LinearChannelType) : undefined,
    blockFastForward: metas['Block_Fast_Forward'] ? (metas['Block_Fast_Forward'] as boolean) : undefined,
    enableTrickPlay: linear.enableTrickPlay,
    enableCatchUp: linear.enableCatchUp,
    metas,
    tags,
    externalId: linear.externalId,
    mediaFilesTypes: linear.mediaFiles.map((mediaFile) => mediaFile.type),
    mosaicSupport: metas['MosaicSupport'] ? (metas['MosaicSupport'] as boolean) : undefined,
    mediaFiles: linear?.mediaFiles,
  };
};

export const assetToMovie = (movie: KalturaMediaAsset): KuxMovieType => {
  const metas = normalizeCategoryMetas(movie.metas as KalturaCategoryMetas);
  const tags = normalizeCategoryTags(movie.tags as KalturaCategoryTags);
  const mediaFile = movie.mediaFiles?.find(({ type }) => KuXDms.filesFormat[FilesFormat.vod].includes(type));

  return {
    description: movie.description,
    id: movie.id,
    tags,
    title: movie.name,
    images: toKuxImages(movie.images),
    metas,
    adult: getAdult(metas),
    parentalRating: getParentalRating(tags),
    mediaType: KuXDms.mediaTypesValue[movie.type],
    type: movie.type,
    mediaFileId: mediaFile?.id,
    duration: metas['duration'] as number,
    mediaFiles: movie?.mediaFiles,
  };
};

export const assetToSeries = (series: KalturaMediaAsset): KuxSeriesType => {
  const metas = normalizeCategoryMetas(series.metas as KalturaCategoryMetas);
  const tags = normalizeCategoryTags(series.tags as KalturaCategoryTags);
  const mediaFile = series.mediaFiles?.find(({ type }) => KuXDms.filesFormat[FilesFormat.trailer].includes(type));
  const seriesId = metas['SeriesID'] as string;

  return {
    description: series.description,
    id: series.id,
    tags,
    title: series.name,
    images: toKuxImages(series.images),
    metas,
    adult: getAdult(metas),
    parentalRating: getParentalRating(tags),
    mediaType: KuXDms.mediaTypesValue[series.type],
    type: series.type,
    seriesId,
    mediaFileId: mediaFile?.id,
    mediaFiles: series?.mediaFiles,
  };
};

export const assetToEpisode = (episode: KalturaMediaAsset, additionalData?: AdditionalData): KuxEpisodeType => {
  const metas = normalizeCategoryMetas(episode.metas as KalturaCategoryMetas);
  const tags = normalizeCategoryTags(episode.tags as KalturaCategoryTags);
  const mediaFile = episode.mediaFiles?.find(({ type }) => KuXDms.filesFormat[FilesFormat.vod].includes(type));
  const { seriesId, episodeNumber, seasonNumber, episodeName } = getEpisodeMetas(metas);

  return {
    id: episode.id,
    adult: getAdult(metas),
    description: episode.description,
    images: toKuxImages(episode.images),
    metas,
    tags,
    mediaType: KuXDms.mediaTypesValue[episode.type],
    type: episode.type,
    title: episode.name,
    seriesId,
    episodeNumber,
    seasonNumber,
    episodeName,
    parentalRating: getParentalRating(tags),
    mediaFileId: mediaFile?.id,
    duration: metas['duration'] as number,
    isGroupedSeries: additionalData?.isGroupedGrid || false,
    mediaFiles: episode?.mediaFiles,
  };
};

export const assetToLinkAsAsset = (link: KalturaMediaAsset, data?: AdditionalData): KuxLinkAsAssetType => {
  const metas = normalizeCategoryMetas(link.metas as KalturaCategoryMetas);
  const tags = normalizeCategoryTags(link.tags as KalturaCategoryTags);
  return {
    type: link.type,
    id: link.id,
    linkItem: parseMenuItem(link, data?.presentationType),
    images: toKuxImages(link.images),
    title: link.name,
    adult: false,
    parentalRating: 0,
    metas,
    mediaType: KuXDms.mediaTypesValue[link.type],
    tags,
  };
};

export const assetToPackage = (asset: KalturaMediaAsset): KuxPackage => {
  const metas = normalizeCategoryMetas(asset.metas as KalturaCategoryMetas);
  const tags = normalizeCategoryTags(asset.tags);

  return {
    id: asset.id,
    startDate: asset.startDate,
    endDate: asset.endDate,
    title: asset.name,
    description: asset.description,
    image: asset.images[0]?.url,
    images: toKuxImages(asset.images),
    priceDescription: metas['PriceDescription'] as string,
    disclaimer: metas['Disclaimer'] as string,
    subscriptionId: metas['SubscriptionID'] as number,
    basePackageId: metas['BasePackageId'] as number,
    catalogStartDateTime: metas['CatalogStartDateTime'] as string,
    playbackEndDateTime: metas['PlaybackEndDateTime'] as string,
    tags,
    metas,
    adult: getAdult(metas),
    parentalRating: 0,
    type: asset.type,
    mediaType: KuXDms.mediaTypesValue[asset.type],
    externalId: asset.externalId,
  };
};

export const assetToPerson = (asset: KalturaMediaAsset): KuxPerson => {
  const metas = normalizeCategoryMetas(asset.metas as KalturaCategoryMetas);
  const tags = normalizeCategoryTags(asset.tags);

  return {
    id: asset.id,
    description: asset.description,
    images: toKuxImages(asset.images),
    mediaType: KuXDms.mediaTypesValue[asset.type],
    metas,
    tags,
    title: asset.name,
    type: asset.type,
    adult: false,
    parentalRating: 0,
  };
};

export const assetToLogo = (asset: KalturaMediaAsset): KuxLogo => {
  const metas = normalizeCategoryMetas(asset.metas as KalturaCategoryMetas);
  const tags = normalizeCategoryTags(asset.tags);

  return {
    id: asset.id,
    title: asset.name,
    description: asset.description,
    images: toKuxImages(asset.images),
    mediaType: KuXDms.mediaTypesValue[asset.type],
    type: asset.type,
    metas,
    tags,
    adult: false,
    parentalRating: 0,
  };
};

interface getParserAssetMapInterface {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string | number]: (...params: any) => any;
}

const getParserAssetMap = (): {
  [type in Partial<MediaType>]?: <T extends KalturaAsset, U extends KuxMediaAssetType>(asset: T) => U;
} => ({
  [KuXDms.mediaTypes.Program]: assetToProgram,
  [KuXDms.mediaTypes.Linear]: assetToLinear,
  [KuXDms.mediaTypes.Movie]: assetToMovie,
  [KuXDms.mediaTypes.Episode]: assetToEpisode,
  [KuXDms.mediaTypes.Series]: assetToSeries,
  [KuXDms.mediaTypes.Package]: assetToPackage,
  [KuXDms.mediaTypes.Person]: assetToPerson,
  [KuXDms.mediaTypes.Logo]: assetToLogo,
  [KuXDms.mediaTypes.in_application_link_asset]: assetToLinkAsAsset,
});

export const assetToType = <P extends KalturaAsset, U extends KuxMediaAssetType>(
  asset: P,
  additionalData?: AdditionalData,
): U | undefined => {
  const parserAssetMap: getParserAssetMapInterface = getParserAssetMap();
  if (!parserAssetMap[asset.type]) {
    // TODO: log configuration issue
    return undefined;
  }
  if (asset?.name === '') {
    // TODO: log configuration issue
    return undefined;
  }
  return parserAssetMap[asset.type](asset, additionalData);
};

export const assetsToType = <P extends KalturaAsset, U extends KuxMediaAssetType>(
  assets: P[],
  additionalData?: AdditionalData,
): (U | undefined)[] => {
  return assets.map((asset) => assetToType<P, U>(asset, additionalData)).filter(Boolean);
};

export const assetToRecord = (record: KalturaRecording): KuxRecord | undefined => {
  if (!record) return;
  return {
    id: record.id,
    type: record.type,
    status: record.status,
    assetId: record.assetId,
    createDate: record.createDate,
    viewableUntilDate: record?.viewableUntilDate,
    expiredInDays: record?.viewableUntilDate && getDaysBetweenTwoDate(new Date(record.viewableUntilDate * 1000)),
  };
};

export const assetToSubscription = (asset: KalturaSubscription): KuxSubscription => {
  return {
    id: asset.id,
    dependencyType: asset.dependencyType,
    title: asset.name,
    externalId: asset.externalId,
    description: asset.description,
    price: {
      amount: asset.price.price.amount,
      currency: asset.price.price.currency,
      currencySign: asset.price.price.currencySign,
    },
  };
};

export const assetToPpv = (ppv: KalturaPpv, price: KalturaPrice): KuxPpv => {
  return {
    id: ppv.id,
    viewLifeCycle: ppv.usageModule?.viewLifeCycle,
    isSubscriptionOnly: ppv.isSubscriptionOnly,
    price: {
      amount: price?.amount,
      currency: price?.currency,
      currencySign: price?.currencySign,
    },
    fullPrice: {
      amount: ppv.price?.price?.amount,
      currency: ppv.price?.price?.currency,
      currencySign: ppv.price?.price?.currencySign,
    },
  };
};
